import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { projectsAPI, reportsAPI, filesAPI } from '../../services/api';
import {
  Upload,
  MapPin,
  Camera,
  File,
  X,
  CheckCircle,
  Loader2,
  Navigation
} from 'lucide-react';
import { toast } from 'sonner';

export default function SubmitReportPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [indicatorVerifications, setIndicatorVerifications] = useState([]);
  
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    content: '',
    findings: '',
    recommendations: '',
    activity_type: '',
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.list();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        });
        toast.success('Location captured successfully');
      },
      (error) => {
        toast.error('Unable to get your location');
        console.error('Geolocation error:', error);
      }
    );
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    for (const file of files) {
      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }
    }

    setUploading(true);
    
    try {
      for (const file of files) {
        const response = await filesAPI.upload(file, formData.project_id);
        setUploadedFiles(prev => [...prev, {
          file_id: response.data.file_id,
          filename: response.data.filename,
          path: response.data.path
        }]);
      }
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }

    if (!formData.title || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const reportData = {
        project_id: formData.project_id,
        title: formData.title,
        content: formData.content,
        findings: formData.findings || null,
        recommendations: formData.recommendations || null,
        activity_type: formData.activity_type || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        evidence_file_ids: uploadedFiles.map(f => f.file_id),
        indicator_verification: indicatorVerifications.map(iv => ({
          indicator_name: iv.indicator_name,
          donor_value: iv.donor_value,
          verified_value: iv.verified_value || null,
          agent_notes: iv.agent_notes || null
        }))
      };

      // Create the report (draft)
      const createRes = await reportsAPI.create(reportData);
      const reportId = createRes.data.report_id;

      // Auto-submit for admin review
      await reportsAPI.submit(reportId);

      toast.success('Report submitted for admin review!');
      navigate('/agent/reports');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 page-enter" data-testid="submit-report-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">Submit Field Report</h1>
        <p className="text-slate-500 mt-1">Document your field activities with photos and location data</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Project Selection */}
        <Card className="stat-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Select Project</CardTitle>
            <CardDescription>Choose the project this report is for</CardDescription>
          </CardHeader>
          <CardContent>
            <Select
              value={formData.project_id}
              onValueChange={(v) => {
                setFormData({ ...formData, project_id: v });
                const selectedProject = projects.find(p => p.project_id === v);
                if (selectedProject?.indicators?.length > 0) {
                  setIndicatorVerifications(selectedProject.indicators.map(ind => ({
                    indicator_name: ind.name,
                    donor_value: ind.target_value || ind.target || '',
                    description: ind.description || '',
                    verified_value: '',
                    agent_notes: ''
                  })));
                } else {
                  setIndicatorVerifications([]);
                }
              }}
            >
              <SelectTrigger data-testid="project-select">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Report Details */}
        <Card className="stat-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Report Details</CardTitle>
            <CardDescription>Describe your field activity</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Report Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Weekly Progress Update - Water Well Construction"
                required
                data-testid="report-title-input"
              />
            </div>

            <div>
              <Label htmlFor="activity_type">Activity Type</Label>
              <Select
                value={formData.activity_type}
                onValueChange={(v) => setFormData({ ...formData, activity_type: v })}
              >
                <SelectTrigger data-testid="activity-type-select">
                  <SelectValue placeholder="Select activity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="site_visit">Site Visit</SelectItem>
                  <SelectItem value="training">Training Session</SelectItem>
                  <SelectItem value="distribution">Distribution</SelectItem>
                  <SelectItem value="construction">Construction Progress</SelectItem>
                  <SelectItem value="survey">Survey/Data Collection</SelectItem>
                  <SelectItem value="meeting">Community Meeting</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="content">Description *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Describe what was accomplished, any challenges faced, and observations..."
                rows={6}
                required
                data-testid="report-content-input"
              />
            </div>

            <div>
              <Label htmlFor="findings">Key Findings</Label>
              <Textarea
                id="findings"
                value={formData.findings}
                onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                placeholder="Summarize the key findings from your field activity..."
                rows={3}
                data-testid="report-findings-input"
              />
            </div>

            <div>
              <Label htmlFor="recommendations">Recommendations</Label>
              <Textarea
                id="recommendations"
                value={formData.recommendations}
                onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                placeholder="Provide any recommendations based on your findings..."
                rows={3}
                data-testid="report-recommendations-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card className="stat-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Location</CardTitle>
            <CardDescription>Add GPS coordinates for verification</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="-34.6037"
                  data-testid="latitude-input"
                />
              </div>
              <div>
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="-58.3816"
                  data-testid="longitude-input"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={handleGetLocation}
              data-testid="get-location-btn"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Get Current Location
            </Button>
          </CardContent>
        </Card>

        {/* Indicator Verification */}
        {indicatorVerifications.length > 0 && (
          <Card className="stat-card mb-6" data-testid="indicator-verification-section">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Indicator Verification</CardTitle>
              <CardDescription>Verify the donor's project indicators with field-observed values</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {indicatorVerifications.map((iv, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg border border-slate-200 bg-slate-50/50 space-y-3"
                  data-testid={`indicator-row-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{iv.indicator_name}</p>
                      {iv.description && (
                        <p className="text-xs text-slate-500 mt-0.5">{iv.description}</p>
                      )}
                    </div>
                    <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                      Target: {iv.donor_value}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">Verified Value</Label>
                      <Input
                        placeholder="Enter field-observed value"
                        value={iv.verified_value}
                        onChange={(e) => {
                          const updated = [...indicatorVerifications];
                          updated[idx] = { ...updated[idx], verified_value: e.target.value };
                          setIndicatorVerifications(updated);
                        }}
                        data-testid={`indicator-verified-value-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Agent Notes</Label>
                      <Input
                        placeholder="Observation notes..."
                        value={iv.agent_notes}
                        onChange={(e) => {
                          const updated = [...indicatorVerifications];
                          updated[idx] = { ...updated[idx], agent_notes: e.target.value };
                          setIndicatorVerifications(updated);
                        }}
                        data-testid={`indicator-agent-notes-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Evidence Upload */}
        <Card className="stat-card mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Evidence</CardTitle>
            <CardDescription>Upload photos and documents as verification evidence</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <div className="flex flex-col items-center">
                  {uploading ? (
                    <Loader2 className="w-12 h-12 text-slate-400 animate-spin" />
                  ) : (
                    <Camera className="w-12 h-12 text-slate-400" />
                  )}
                  <p className="mt-4 text-sm font-medium text-slate-700">
                    {uploading ? 'Uploading...' : 'Click to upload photos or documents'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    PNG, JPG, PDF, DOC up to 10MB
                  </p>
                </div>
              </label>
            </div>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {uploadedFiles.map((file) => (
                  <div
                    key={file.file_id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <File className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-700">{file.filename}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveFile(file.file_id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-report-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
